import React from 'react';
import { Timeline } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderStepContext } from '../orderStepContext';
import { InfoList, KeyValueList, StepSection } from '../OrderProcessParts';
import { OrderIssueProgress, useQuantityFormatter } from '../OrderProcessTables';
import { findLatestStatusEntry } from '../orderOfferAcceptance';
import { formatDateEuropean, formatDateTimeEuropean } from '../../../../utils/dateUtils';

/**
 * Dostarczone (makieta `sDel`): the delivery day chosen in „Potwierdź
 * dostarczenie” and its note, what was issued, and the status history. No scan
 * of the signed WZ is kept (owner 2026-10-05).
 */
export default function DeliveredStep({ ctx }: { ctx: OrderStepContext }) {
  const { t } = useTranslation(['orders']);
  const quantity = useQuantityFormatter();
  const { order, facts } = ctx;
  const issued = facts.issuedWz;
  const deliveredEntry = findLatestStatusEntry(order.statusHistory, 'DELIVERED')
    ?? findLatestStatusEntry(order.statusHistory, 'PARTIALLY_DELIVERED');
  const history = [...(order.statusHistory ?? [])]
    .sort((a, b) => new Date(a.changedAt).getTime() - new Date(b.changedAt).getTime());
  const lines = ctx.warehouse?.lines ?? null;
  const totalIssued = lines
    ? Object.entries(lines.reduce<Record<string, number>>((acc, line) => {
      acc[line.unit] = (acc[line.unit] || 0) + (line.issuedQuantity ?? 0);
      return acc;
    }, {})).map(([unit, value]) => quantity(value, unit)).join(' + ')
    : '—';

  return (
    <div className="zk-col" data-testid="zk-step-DELIVERED">
      <div className="zk-banner zk-banner--success">
        <CheckCircleOutlined aria-hidden="true" />
        <span className="zk-banner__text">{t('orders:process.delivered.banner')}</span>
      </div>
      <div className="zk-grid">
        <div className="zk-col">
          <StepSection title={t('orders:process.delivered.proofTitle')} testId="zk-delivery-proof">
            <KeyValueList rows={[
              [t('orders:process.delivered.date'), order.actualDelivery ? formatDateEuropean(order.actualDelivery) : '—'],
              [t('orders:process.delivered.notes'), deliveredEntry?.notes || '—'],
            ]} />
            {lines && (
              <>
                <h4 className="zk-section__group-title">{t('orders:process.delivered.issuedTitle')}</h4>
                <OrderIssueProgress lines={lines} products={order.productsLoaded ? order.products : []} />
              </>
            )}
          </StepSection>
        </div>
        <div className="zk-col">
          <StepSection title={t('orders:process.delivered.summaryTitle')} icon={<CheckCircleOutlined />}>
            <InfoList rows={[
              [t('orders:process.delivered.wzDocuments'), issued.map((document) => document.wzNumber).join(', ') || '—'],
              [t('orders:process.delivered.date'), order.actualDelivery ? formatDateEuropean(order.actualDelivery) : '—'],
              [t('orders:process.delivered.lineCount'), String(order.productsLoaded ? order.products.length : 0)],
              [t('orders:process.delivered.totalIssued'), totalIssued],
            ]} />
          </StepSection>
          <StepSection title={t('orders:process.delivered.historyTitle')} icon={<ClockCircleOutlined />}>
            <Timeline
              items={history.map((entry) => ({
                key: entry.id,
                color: 'green',
                children: (
                  <div>
                    <strong>{t(`orders:statusOptions.${entry.status}`)}</strong>
                    <div className="zk-wz__meta">{formatDateTimeEuropean(entry.changedAt)} · {entry.changedBy?.name || t('orders:orderDetail.historyAuthorUnknown')}</div>
                  </div>
                ),
              }))}
            />
          </StepSection>
        </div>
      </div>
    </div>
  );
}
